import cntl from "cntl";
import { Pagination } from "..";
import { PropTypes } from "prop-types";
import { useEffect, useState, useMemo } from "react";
import { PAGINATION_VARIANTS } from "../constants";
import { flexRender, useReactTable, getCoreRowModel, getSortedRowModel } from "@tanstack/react-table";
import { IconArrowDown, IconArrowsUpDown, IconArrowUp } from "@tabler/icons-react";

const propTypes = {
  to: PropTypes.number,
  fetch: PropTypes.func,
  from: PropTypes.number,
  total: PropTypes.number,
  hasHeader: PropTypes.bool,
  settings: PropTypes.object,
  pageCount: PropTypes.number,
  className: PropTypes.string,
  hasPagination: PropTypes.bool,
  paginationSize: PropTypes.number,
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  variantPagination: PropTypes.oneOf([
    PAGINATION_VARIANTS.card_button,
    PAGINATION_VARIANTS.card_center,
    PAGINATION_VARIANTS.card_default,
    PAGINATION_VARIANTS.card_left,
    PAGINATION_VARIANTS.card_right,
    PAGINATION_VARIANTS.page_button,
    PAGINATION_VARIANTS.page_default,
  ]),
};

function DataGrid({
  to,
  data,
  from,
  total,
  fetch,
  columns,
  settings,
  className,
  pageCount,
  hasHeader = true,
  variantPagination,
  paginationSize = 15,
  hasPagination = true,
}) {
  const [sorting, setSorting] = useState([]);
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: paginationSize,
  });

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  const wrapperTableAndPagination = () => cntl`
    flex
    flex-col
    ${className ? className : undefined}
  `;

  const wrapperTable = () => cntl`
    w-full
    ${settings?.table?.bgColor}
  `;

  const headerCn = () => cntl`
    h-11
    text-xs
    border-b
    font-medium
    text-grey-600
    border-b-grey-200 
    ${settings?.table?.header?.bgColor || `bg-grey-50`}
  `;

  const thHeader = () => cntl`
    px-6
    text-left
  `;

  const thHeaderSort = ({ textAlign }) => cntl`
    flex
    gap-x-1
    flex-row
    select-none
    items-center
    cursor-pointer
    ${textAlign?.toLowerCase() === "left" ? cntl`justify-start` : cntl`justify-end`}
  `;

  const thHeaderNoSort = ({ textAlign }) => cntl`
    flex
    gap-x-1
    flex-row
    ${textAlign?.toLowerCase() === "left" ? cntl`justify-start` : cntl`justify-end`}
  `;

  const iconCn = () => cntl`
    w-4
    h-4
  `;

  const bodyCn = () => cntl`
    bg-transparent
  `;

  const trBody = () => cntl`
    border-b
    last:border-b-0
    hover:bg-grey-50
    border-b-grey-200
    ${settings?.table?.row?.height || `h-[4.5rem]`}
  `;

  const table = useReactTable({
    data,
    columns,
    pageCount,
    autoResetPage: false,
    manualPagination: true,
    onSortingChange: setSorting,
    initialState: { pageIndex: 0 },
    getSubRows: (row) => row.subRows,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      pagination,
    },
    defaultColumn: {
      minSize: 0,
      size: 0,
    },
  });

  useEffect(() => {
    fetch({ pageIndex: pagination.pageIndex, pageSize: pagination.pageSize });
  }, [fetch, pagination.pageIndex, pagination.pageSize]);

  return (
    <div className={wrapperTableAndPagination()}>
      <table className={wrapperTable()}>
        {hasHeader && (
          <thead className={headerCn()}>
            {table?.getHeaderGroups() &&
              table?.getHeaderGroups()?.map((headerGroup) => {
                return (
                  <tr key={headerGroup.id}>
                    {headerGroup?.headers?.map((header) => {
                      return (
                        <th key={header.id} className={thHeader()}>
                          <div
                            {...{
                              className: header.column.getCanSort()
                                ? thHeaderSort({
                                    textAlign: header.column.columnDef.textHeaderAlign || "left",
                                  })
                                : thHeaderNoSort({
                                    textAlign: header.column.columnDef.textHeaderAlign || "left",
                                  }),
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {header.column.getCanSort() && (
                              <div className={iconCn()}>
                                {!header.column.getIsSorted() && <IconArrowsUpDown className={iconCn()} />}
                                {{
                                  asc: <IconArrowDown className={iconCn()} />,
                                  desc: <IconArrowUp className={iconCn()} />,
                                }[header.column.getIsSorted()] ?? null}
                              </div>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
          </thead>
        )}
        <tbody className={bodyCn()}>
          {table?.getRowModel()?.rows?.map((row) => {
            return (
              <tr key={row.id} className={trBody()}>
                {row?.getVisibleCells()?.map((cell) => {
                  return <td key={cell.id}>{flexRender(cell?.column?.columnDef?.cell, cell?.getContext())}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {hasPagination && (
        <Pagination
          to={to}
          from={from}
          total={total}
          pageCount={pageCount}
          variant={variantPagination}
          paginationSize={paginationSize}
          nextPage={() => table.nextPage()}
          canNextPage={table.getCanNextPage()}
          previousPage={() => table.previousPage()}
          canPreviousPage={table.getCanPreviousPage()}
          gotoPage={(index) => table.setPageIndex(index)}
          pageIndex={table.getState().pagination.pageIndex}
        />
      )}
    </div>
  );
}

DataGrid.propTypes = propTypes;
export default DataGrid;
